/** @jsxImportSource solid-js */

/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { JSXElement } from 'solid-js';
import { Component, Show, For, createSignal, untrack } from 'solid-js';
import { translate as t } from '@fulcrum/lib';
import { noShadowDOM } from 'solid-element';
const DefaultItemLimit = 5;

export interface LimitVisibleItemsProps {
  /** Value to indicate how many items are shown. Optional, default value is 5. */
  itemLimit?: number;
  /** Optional style overriding. */
  className?: string;
  /** Optional show more string */
  showMore?: string;
  /** Optional show less string */
  showLess?: string;
  /** Content to be limited */
  content?: JSXElement;
}

const LimitVisibleItems: Component<LimitVisibleItemsProps> = (
  props
): JSXElement => {
  noShadowDOM();

  let containerRef: HTMLDivElement | undefined;
  const [showAll, setShowAll] = createSignal(false);

  const getItemLimit = () => props.itemLimit || DefaultItemLimit;
  const getClassName = () => props.className || '';
  const getShowMore = () => props.showMore;
  const getShowLess = () => props.showLess;

  // Convert children to array if content is provided
  const getChildren = () => {
    const contentValue = untrack(() => props.content);
    if (contentValue) {
      // If content is a single element, wrap in array
      if (Array.isArray(contentValue)) {
        return contentValue;
      }
      return [contentValue];
    }
    return [];
  };

  const children = getChildren();
  const renderLessMoreButton = () => children.length > getItemLimit();
  const items = () =>
    showAll() || !renderLessMoreButton()
      ? children
      : children.slice(0, getItemLimit());

  const toggleLessMore = () => {
    const newShowAll = !showAll();
    setShowAll(newShowAll);

    // Emit custom event for React wrapper
    if (containerRef) {
      const event = new CustomEvent('toggle', {
        detail: { showAll: newShowAll },
        bubbles: true,
      });
      containerRef.dispatchEvent(event);
    }
  };

  const getShowMoreLabel = (hiddenItems: number): string => {
    if (getShowMore()) {
      return getShowMore()!.replace('{{count}}', hiddenItems.toString());
    }
    return t('TC.Common.ShowAll');
  };

  const getShowLessLabel = (): string => {
    return getShowLess() || t('TC.Common.ShowLess');
  };

  return (
    <div ref={containerRef} class={getClassName()}>
      <For each={items()}>{(child) => child}</For>
      <Show when={renderLessMoreButton()}>
        <div class="label-group">
          <button
            class="button link-primary inline-left"
            type="button"
            onClick={toggleLessMore}
            data-cy={showAll() ? 'show-less' : 'show-more'}
          >
            {showAll()
              ? getShowLessLabel()
              : getShowMoreLabel(children.length - getItemLimit())}
          </button>
        </div>
      </Show>
    </div>
  );
};

export default LimitVisibleItems;
